import { Box, Container, Typography } from '@mui/material';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useTranslation } from 'react-i18next';
import { ProofButton } from '../../ui/button/ProofButton';
import palette from '../../ui/palette';
import { LinkProblem } from '../../utils/linkProblem';

export type LinkUnavailableReason = LinkProblem | 'load_failed';

interface Props {
  reason: LinkUnavailableReason;
  onRetry?: VoidFunction;
}

const copy: Record<LinkUnavailableReason, { title: string; desc: string }> = {
  link_invalid: { title: 'verification.linkUnavailable.invalidTitle', desc: 'verification.linkUnavailable.newLinkDesc' },
  link_expired: { title: 'verification.linkUnavailable.expiredTitle', desc: 'verification.linkUnavailable.newLinkDesc' },
  application_closed: { title: 'verification.linkUnavailable.closedTitle', desc: 'verification.linkUnavailable.closedDesc' },
  load_failed: { title: 'verification.linkUnavailable.loadFailedTitle', desc: 'verification.linkUnavailable.loadFailedDesc' },
};

/** Shown in place of the flow when the applicant's link can't be used, and for unknown URLs. */
export default function LinkUnavailable({ reason, onRetry }: Props) {
  const { t } = useTranslation();
  const { title, desc } = copy[reason];
  const Icon = reason === 'load_failed' ? ErrorOutlineRoundedIcon : LinkOffRoundedIcon;

  return (
    <Container maxWidth="xs" sx={{ px: 3, textAlign: 'center' }}>
      <Icon sx={{ fontSize: 48, color: palette.grey[40], mb: 2 }} />
      <Typography variant="h2" sx={{ fontWeight: 500, mb: { xs: 2, md: 2.5 } }}>
        {t(title)}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }}>
        {t(desc)}
      </Typography>
      {onRetry && (
        <Box>
          <ProofButton variant="contained" fullWidth onClick={onRetry}>
            {t('page.tryAgain')}
          </ProofButton>
        </Box>
      )}
    </Container>
  );
}
